import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { queryHashGenerator } from '../../../utils/queryHash';
import { scaleZScore, zScore } from '../../../utils/scoring';
import { testMetaString } from '../../../utils/testMetaString';
import {
  Entity,
  EntityType,
  EZScorePreference,
  IAggregateWithMetric,
  IAthlete,
  IComputedAggregate,
  ICrossTestMetricSelection,
  ISelectableEntity,
  ITag,
} from '../../types';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import { entitiesProvider, IEntities } from '../entitiesProvider';
import { IComparisonPopulationSetting } from '../settings/comparisonPopulationSettingsProvider';
import {
  ITsaReportSettings,
  ITsaReportSettingsState,
  tsaReportSettingsProvider,
} from '../settings/reports/tsaReportSettingsProvider';
import { ISettingsTemplate } from '../settings/reports/types';
import { calculateTsa } from '../utils/calculateTsa';
import { determinePopulation } from '../utils/determinePopulation';
import {
  convertTimeframeToLocalTime,
  formatTimeframe,
} from '../utils/formatTimeframe';
import { gatherQueryHashes } from '../utils/gatherQueryHashes';
import { getSessionRangeTimestamps } from '../utils/getSessionRangeTimestamps';
import { IReportState, IValidationResult } from './types';

export interface IZScore {
  change: number;
  changePercentage: number;
  label: string;
  metric: ICrossTestMetricSelection;
  population: IAggregateWithMetric;
  populationCount: number;
  populationScore: number;
  populationTimeframe: string;
  roundingPrecision: number;
  target: IAggregateWithMetric;
  targetCount: number;
  targetTimeframe: string;
  testMeta: string;
  units: string;
  zscore: number;
}

interface IZScores {
  entity: Entity;
  zScores: IZScore[];
}

export interface ITsaReportState extends IReportState, ITsaReportSettingsState {
  settings: ISettingsTemplate<ITsaReportSettings>;
  quartileData: IQuartile;
  tsaResults: ITsaResult[];
}

export interface ITsaResult {
  entity: Entity;
  grouping?: number;
  quartile?: number;
  rank?: number;
  tsa: number;
}

interface IEntityQueryArg {
  entity: Entity;
  id: string;
  type: EntityType;
}

interface ITargetData {
  data: ICrossTestAggregates;
  entity: Entity;
}

interface IEntitiesStageData extends ITsaReportSettingsState {
  entityQueries: IEntityQueryArg[];
}

interface ITargetStageData extends ITsaReportSettingsState {
  hashes: string[];
  targetData: ITargetData[];
}

interface IPopulationStageData extends ITargetStageData {
  populationData: ICrossTestAggregates;
}

export interface IQuartile {
  median?: number;
  highQuartile?: number;
  lowQuartile?: number;
}

export const tsaReportProvider = (
  entityType: EntityType,
  entity: Entity
): Observable<ITsaReportState> => {
  return tsaReportSettingsProvider().pipe(
    /// this stage gets the individual athletes if selected entity is a group or a team
    switchMap(
      (settings: ITsaReportSettingsState): Observable<IEntitiesStageData> => {
        if (entityType === 'groups' || entityType === 'teams') {
          return entitiesProvider().pipe(
            map((entities: IEntities): IEntitiesStageData => {
              /// filter the athletes that are members of the group or team
              const athletes = entities.athletes.filter(
                (a: IAthlete): boolean => !!a?.[entityType]?.includes(entity.id)
              );
              return {
                ...settings,
                entityQueries: athletes.map(
                  (mEntity: IAthlete): IEntityQueryArg => ({
                    id: mEntity.id,
                    type: 'athletes',
                    entity: mEntity,
                  })
                ),
              };
            })
          );
        } else {
          ///  the selected entity is an athlete
          return of({
            ...settings,
            entityQueries: [{ id: entity.id, type: entityType, entity }],
          });
        }
      }
    ),
    /// this stage fetches the target data for the entities
    switchMap(
      (
        settingsAndEntities: IEntitiesStageData
      ): Observable<ITargetStageData> => {
        const settingsValidation = validateSettings(settingsAndEntities);

        if (settingsValidation.isValid) {
          if (settingsAndEntities.entityQueries.length > 0) {
            const queryHashes = gatherQueryHashes(
              settingsAndEntities?.selected?.settings?.metrics
            );

            return combineLatest(
              settingsAndEntities.entityQueries.map(
                (qe: IEntityQueryArg): Observable<ITargetData> => {
                  const { targetTimeframe } = settingsAndEntities;
                  return crossTestAggregates(
                    queryHashes ?? [],
                    qe.id,
                    qe.type as EntityType,
                    targetTimeframe.dateType,
                    targetTimeframe.dateRange
                  ).pipe(
                    map(
                      (data: ICrossTestAggregates): ITargetData => ({
                        entity: qe.entity,
                        data,
                      })
                    )
                  );
                }
              )
            ).pipe(
              map((targetData: ITargetData[]): ITargetStageData => {
                return {
                  targetData,
                  ...settingsAndEntities,
                  hashes: queryHashes,
                };
              })
            );
          } else {
            const targetStageData: ITargetStageData = {
              ...settingsAndEntities,
              hashes: null,
              targetData: [],
            };
            return of(targetStageData);
          }
        } else {
          return of({
            ...settingsAndEntities,
            targetData: [],
            hashes: null,
          });
        }
      }
    ),
    /// this stage fetches the population data
    switchMap((data: ITargetStageData): Observable<IPopulationStageData> => {
      if (isEmpty(data?.comparisonPopulation)) {
        return of({
          ...data,
          populationData: null,
        });
      }
      const [populationEntityType, populationEntityId] = determinePopulation(
        data?.comparisonPopulation?.entity,
        {
          entityId: entity.id,
          entityType,
        }
      );

      return crossTestAggregates(
        data.hashes ?? [],
        populationEntityId,
        populationEntityType as EntityType,
        data.comparisonPopulation?.dateRangeType,
        data?.comparisonPopulation?.dateRangeValues
      ).pipe(
        map((populationData: ICrossTestAggregates): IPopulationStageData => {
          return {
            ...data,
            populationData,
          };
        })
      );
    }),
    /// this stage creates the finalized report data
    map((results: IPopulationStageData): ITsaReportState => {
      const timeframe =
        results.targetTimeframe?.dateType?.value === 'custom'
          ? formatTimeframe(
              results.targetTimeframe?.dateRange?.from?.value,
              results.targetTimeframe?.dateRange?.to?.value
            )
          : results.targetTimeframe?.dateType?.label;

      // This series of function calls creates the TSA array and quartile data
      const zScores = makeZScores(results);

      const intermediateResults = makeTsaResults(zScores);
      const quartileData = calcMedianAndQuartiles(intermediateResults);
      const tsaResults = addQuartileToAthlete(
        intermediateResults,
        quartileData
      );

      /// validate settings
      const settingsValidation = validateSettings(results);
      if (!settingsValidation.isValid) {
        return {
          ...settingsValidation,
          selected: results.selected,
          comparisonPopulation: results.comparisonPopulation,
          quartileData,
          settings: results.selected,
          targetTimeframe: results.targetTimeframe,
          templates: results.templates,
          timeframe,
          tsaResults,
        };
      }

      /// validate data
      const dataValidation = validateData(tsaResults);
      const localComparisonTimeframe = convertTimeframeToLocalTime({
        dateType: results.comparisonPopulation?.dateRangeType,
        dateRange: results.comparisonPopulation?.dateRangeValues,
      });

      return {
        settings: results.selected,
        targetTimeframe: convertTimeframeToLocalTime(results.targetTimeframe),
        comparisonPopulation: {
          entity: results.comparisonPopulation.entity,
          dateRangeType: localComparisonTimeframe.dateType,
          dateRangeValues: localComparisonTimeframe.dateRange,
        },
        quartileData,
        timeframe,
        ...dataValidation,
        tsaResults,
        selected: results.selected,
        templates: results.templates,
      };
    })
  );
};

const validateData = (results: ITsaResult[]): IValidationResult => {
  if (results.length > 0) {
    return {
      isValid: true,
    };
  } else {
    return {
      isValid: false,
      errorCode: 'insufficient-data',
    };
  }
};

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateSettings = (
  settings: ITsaReportSettingsState
): IValidationResult => {
  if (!settings?.selected) {
    return {
      isValid: false,
      errorCode: 'missing-template',
    };
  }

  return { isValid: true };
};

/**
 * @description generates the individual z-score result for each selected metric in the settings
 * @param settings
 * @param aggregates
 * @returns
 */
const makeZScore = (
  tsaSettings: ITsaReportSettings,
  target: ITargetData,
  populationSettings: IComparisonPopulationSetting,
  populationAggregates: ICrossTestAggregates
): IZScore[] => {
  return tsaSettings?.metrics?.map(
    (selection: ICrossTestMetricSelection): IZScore => {
      const queryHash = queryHashGenerator(
        selection.testType?.value,
        selection?.tags?.map((se: ISelectableEntity): string => se.value) ?? []
      );

      const targetTestType = target?.data?.[queryHash]?.combined.data;

      const targetData = targetTestType?.[selection?.metric?.value];

      const metricDir = targetData?.direction;

      const populationData =
        populationAggregates?.[queryHash]?.combined?.data?.[
          selection?.metric?.value
        ];

      const meanA = targetData?.mean ?? 0;
      const meanB = populationData?.mean ?? 0;
      const total = meanA + meanB;
      const { start: startA, end: endA } = getSessionRangeTimestamps(
        target?.[queryHash]?.sessions ?? []
      );
      const { start: startB, end: endB } = getSessionRangeTimestamps(
        populationAggregates?.[queryHash]?.sessions ?? []
      );

      const change = meanA - meanB;

      const zscore = zScore(
        meanA,
        meanB,
        populationData?.populationSd ?? 0,
        metricDir
      );

      const testMeta = testMetaString(
        selection.testType?.label,
        selection?.tags?.map(
          (se: ISelectableEntity): ITag => se.entity as ITag
        ) ?? []
      );

      return {
        metric: selection,
        target: targetData,
        targetTimeframe: formatTimeframe(startA, endA),
        targetCount: targetData?.count ?? 0,
        population: populationData,
        populationTimeframe: formatTimeframe(startB, endB),
        populationCount: populationData?.count ?? 0,
        zscore:
          populationSettings.zScorePreference === EZScorePreference.Scaled
            ? scaleZScore(zscore)
            : zscore,
        change,
        changePercentage: (change / total) * 100,
        testMeta,
        label: `${testMeta}:${selection.metric.label}`,
        populationScore:
          populationSettings.zScorePreference === EZScorePreference.Scaled
            ? 50
            : 0,
        units: targetData?.units ? targetData?.units : populationData?.units,
        roundingPrecision: targetData?.roundingPrecision
          ? targetData?.roundingPrecision
          : populationData?.roundingPrecision,
      };
    }
  );
};

/**
 * @description generates the z-scores for each athlete for each selected metric
 * @param aggregates
 * @returns
 */
const makeZScores = (results: IPopulationStageData): IZScores[] => {
  const metrics = results.hashes;
  const athleteData = results.targetData;

  return athleteData.map((athlete: ITargetData): IZScores => {
    const isValid = metrics.every(
      (metric: string): IComputedAggregate => athlete.data?.[metric].combined
    )
      ? true
      : false;
    if (isValid) {
      const temp = makeZScore(
        results.selected?.settings,
        athlete,
        results.comparisonPopulation,
        results.populationData
      );

      return {
        entity: athlete.entity,
        zScores: temp,
      };
    }
  });
};

/**
 * @description generates the individual TSA result for each athlete from the calculate z-scores
 * @param zscoreArray
 * @returns
 */
const makeTsaResults = (zScoreArray: IZScores[]): ITsaResult[] => {
  const filteredArray = zScoreArray.filter(
    (cell: IZScores): boolean => cell !== undefined
  );
  const tempArray = filteredArray.map((athlete: IZScores): ITsaResult => {
    const tsa = calculateTsa(athlete.zScores);
    return {
      entity: athlete.entity,
      tsa,
    };
  });

  const sortArray = sortBy(tempArray, (o: ITsaResult): number => {
    return -o.tsa;
  });

  return sortArray.map((athlete: ITsaResult, index: number): ITsaResult => {
    return {
      entity: athlete.entity,
      grouping: 1,
      quartile: 0,
      rank: index + 1,
      tsa: +athlete.tsa.toFixed(2),
    };
  });
};

/**
 * @description generates the quartile data from the TSA results: median and high/low quartiles
 * @param tsaResultsArray
 * @returns
 */
const calcMedianAndQuartiles = (array: ITsaResult[]): IQuartile => {
  if (array.length !== 0) {
    let median: number = null;
    const length: number = array.length;
    const mid: number = Math.floor(length / 2);
    const quartile: number = Math.floor(length / 4);

    if (length % 2 !== 0) {
      median = +array[mid].tsa.toFixed(2);
    } else {
      const lowCell = mid - 1;
      const highCell = mid;
      median = +((array[lowCell].tsa + array[highCell].tsa) / 2).toFixed(2);
    }

    const lowIndex = array.length - 1 - quartile;
    const highIndex = quartile;

    return {
      median,
      highQuartile: array[highIndex].tsa,
      lowQuartile: array[lowIndex].tsa,
    };
  }
  return;
};

/**
 * @description determines which quartile each athlete belongs to and overwrites quartile property
 * @param tsaResultsArray
 * @param quartileData
 * @returns
 */
const addQuartileToAthlete = (
  aA: ITsaResult[],
  qD: IQuartile
): ITsaResult[] => {
  return aA.map((a: ITsaResult): ITsaResult => {
    if (a.tsa < qD.lowQuartile) {
      return { ...a, quartile: 1 };
    } else if (a.tsa >= qD.lowQuartile && a.tsa <= qD.median) {
      return { ...a, quartile: 2 };
    } else if (a.tsa > qD.median && a.tsa <= qD.highQuartile) {
      return { ...a, quartile: 3 };
    } else return { ...a, quartile: 4 };
  });
};
